import React, { useState, useRef, useEffect } from "react";
import ActionDropdown from "../../pages/YourRequest/ActionDropdown";
import SortDropdown from "../../pages/YourRequest/SortDropdown";
import FlightLIsting from "../../pages/YourRequest/FlightLIsting";
import {
  FavouriteResponse,
  Favourites,
  FavouritesParams,
  FlightRequestByIdResponse,
  MyRequestItem,
  MyRequestParams,
  MyRequestResponse,
} from "../../pages/YourRequest/types";
import { addFavourites, deleteFavourites, getFavourites, getUserLoadRequests } from "apis/myRequestsService";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import YourRequestFilter from "../../pages/YourRequest/YourRequestFilter";
import SecondHeader from "../../assets/images/top-header-2.png";
import { AirlineNote } from "types/airlineNotes";
import { useToastStore } from "store/toastStore";
import ReportInaccurateLoadPopup from "../../pages/YourRequest/components/ReportInaccurateLoadPopup";
import RequestUpdateModal from "../../pages/YourRequest/components/RequestUpdateModal";
import DeleteRequestModal from "../../pages/YourRequest/components/DeleteRequestModal";
import ShareRequestModal from "../../pages/YourRequest/components/ShareRequestModal";
import AirlineNotesModal from "components/AirlineNotesModal";
import Pagination from "../../components/Pagination";
import { useClickOutside } from "../../hooks/useClickOutside";

interface FilterState {
  status?: string;
}

interface FlightDetails {
  departure: string;
  status: string;
  timeLeft: string;
  terminal: string;
  gate: string;
  aircraft: string;
}

interface Location {
  code: string;
  city: string;
  time: string;
}

interface FlightStatus {
  text: string;
  color: string;
}

export interface Flight {
  id: number;
  airline: string;
  logoImg: string;
  flightNo: string;
  date: string;
  status: FlightStatus;
  from: Location;
  to: Location;
  duration: string;
  details: FlightDetails;
}

const FavouritesComponent: React.FC = () => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const [flightsDetails, setFlightsDetails] = useState<MyRequestResponse>({
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: 10, // Default value, modify as needed
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [actionIsOpen, setActionIsOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState("departure");
  const [sortOrder, setSortOrder] = useState("asc");
  const [status, setStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [clearSelectAll, setClearSelectAll] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<FilterState>({
    status: "",
  });

  const handleFilterChange = (filters: FilterState) => {
    setStatus(filters.status || ""); // Update the status in parent
    setSelectedFilters(filters); // Store the selected filters
    setShowFilter(false); // Close the filter panel after applying
    if (filters.status === "Expired") {
      setCurrentPage(1);
    }
  };

  const handleActionClick = (action: string) => {
    setSelectedAction(action);
    // Reset the action after processing
    setTimeout(() => setSelectedAction(null), 100); // Small delay to allow execution
  };

  const toggleFilter = () => setShowFilter((prev) => !prev);

  // silent=true skips the global loader — used for the background refresh after
  // an optimistic delete so the page refills without a loader flash.
  const fetchLoadRequests = async (silent = false) => {
    if (!silent) {
      showLoader();
      setLoading(true);
    }
    setError(null);

    try {
      const params: FavouritesParams = {
        page: currentPage,
        pageSize: pageSize,
      };
      const data = await getFavourites(params);
      // If the current page is now empty (e.g. the last favourite was removed),
      // step back a page; the effect refetch will load it.
      if (data.items.length === 0 && currentPage > 1) {
        setCurrentPage((p: number) => p - 1);
        return;
      }
      const transformedData = {
        items: data.items,
        totalCount: data.totalCount, // real total across all pages (not the current page length)
        page: data.page,
        pageSize: data.pageSize,
      };
      setFlightsDetails(transformedData);
    } catch (err) {
      console.error("Error fetching load requests:", err);
      setError("Failed to fetch load requests");
    } finally {
      if (!silent) {
        setLoading(false);
        setTimeout(() => hideLoader(), 500);
      }
    }
  };

  // ✅ Call fetchLoadRequests inside useEffect
  useEffect(() => {
    fetchLoadRequests();
  }, [status, currentPage, sortBy, sortOrder, pageSize]);

  useClickOutside(dropdownRef, () => setOpenDropdown(null), true, "click");

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= Math.ceil((flightsDetails?.totalCount ?? 0) / pageSize)) {
      setCurrentPage(newPage);
    }
    // Reset the "Select All" checkbox
    setClearSelectAll(true);

    // Set it back to false after a brief delay (to allow re-rendering)
    setTimeout(() => setClearSelectAll(false), 0);
  };

  // Change rows-per-page and jump back to the first page.
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Function to generate page numbers to display
  // Function to generate page numbers to display
  return (
    <>
      <div className="">
        <section className="flex flex-wrap gap-3 sm:gap-4 items-center justify-between p-3 sm:p-6">
          <p className="text-[#333333] text-lg font-semibold">Favourites</p>
          {/* <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <button onClick={toggleFilter} className="flex items-center gap-2 p-3 bg-[#FFBC00] rounded-lg">
                <i className="st-filter text-[#F5F9FE]"></i>
                <p className="font-medium text-[#F5F9FE] text-sm">Filters</p>
              </button>
              {showFilter && (
                <div className="absolute left-0 mt-2 w-full">
                  <YourRequestFilter
                    activeFilters={selectedFilters}
                    onClose={() => setShowFilter(false)}
                    onFilterChange={handleFilterChange}
                  />
                </div>
              )}
            </div>
            <div id="dropdown-2" className="z-10 hidden bg-white divide-y divide-gray-100 rounded-lg shadow w-56">
              <ul className="px-4 text-sm text-gray-700" aria-labelledby="dropdownDefaultButton">
                <li>
                  <a href="#" className="flex items-center gap-2 py-2 mt-2">
                    <i className="st-thumbpin text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">PIN Flight</p>
                  </a>
                </li>
                <li>
                  <a data-modal-target="default-modal" data-modal-toggle="default-modal" href="#" className="flex items-center gap-2 py-2">
                    <i className="st-reload text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm cursor-pointer">Request Update</p>
                  </a>
                </li>

                <li>
                  <a href="#" className="flex items-center gap-2 py-2">
                    <i className="st-warning text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Report Inaccurate Load</p>
                  </a>
                </li>
                <li>
                  <a href="#" className="flex items-center gap-2 py-2">
                    <i className="st-enable text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Enable Status Update</p>
                  </a>
                </li>
                <li>
                  <a
                    data-modal-target="default-modal-2"
                    data-modal-toggle="default-modal-2"
                    href="#"
                    className="flex items-center gap-2 py-2"
                  >
                    <i className="st-share text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Share Request</p>
                  </a>
                </li>
                <li>
                  <a
                    data-modal-target="default-modal-1"
                    data-modal-toggle="default-modal-1"
                    href="#"
                    className="flex items-center gap-2 py-2"
                  >
                    <i className="st-delete text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Delete Request</p>
                  </a>
                </li>
              </ul>
            </div>
            <div className="relative">
              <button
                id="dropdownDefaultButton"
                data-dropdown-toggle="dropdown-3"
                className="flex items-center gap-2 p-4 bg-royalblue-200 rounded-lg"
                type="button"
                onClick={() => setIsSortDropdownOpen((prev) => !prev)}
              >
                <i className="st-options text-white text-xs"></i>
              </button>

              <SortDropdown
                isOpen={isSortDropdownOpen}
                setIsOpen={setIsSortDropdownOpen}
                setSortBy={setSortBy}
                setSortOrder={setSortOrder}
                sortBy={sortBy}
                sortOrder={sortOrder} 
              />
            </div>
            <div id="dropdown-3" className="z-10 hidden bg-white divide-y divide-gray-100 rounded-lg shadow w-56">
              <ul className="px-4 text-sm text-gray-700" aria-labelledby="dropdownDefaultButton">
                <p className="py-4 border-b border-gray-200 font-semibold text-grease-100">Sort By</p>
                <li>
                  <a href="#" className="flex items-center gap-2 py-2 mt-2">
                    <i className="st-priority text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Priority</p>
                  </a>
                </li>
                <li>
                  <a href="#" className="flex items-center gap-2 py-2">
                    <i className="st-arrow-slant text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Departure Time</p>
                  </a>
                </li>
                <li>
                  <a href="#" className="flex items-center gap-2 py-2">
                    <i className="st-route text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Route</p>
                  </a>
                </li>
                <li>
                  <a href="#" className="flex items-center gap-2 py-2">
                    <i className="st-flight-slim text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Flight Number</p>
                  </a>
                </li>
              </ul>
            </div>
          </div> */}
        </section>
        <div className="px-4">
          <FlightLIsting
            flights={flightsDetails}
            action={selectedAction}
            clearSelectAll={clearSelectAll}
            fetchLoadRequests={fetchLoadRequests}
            isFavouritesPage={true}
            loading={loading}
            footer={
              flightsDetails?.items?.length > 0 ? (
                <Pagination
                  currentPage={currentPage}
                  totalCount={flightsDetails?.totalCount ?? 0}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  showTotal
                />
              ) : null
            }
          />
        </div>
      </div>
    </>
  );
};

export default FavouritesComponent;
